<script lang="ts" setup>
import type { PropType } from 'vue';
import GStepGrid from '@/components/GStepper/components/GStepGrid.vue';
import GProductCard from '@/components/GProductCard.vue';
import { GButton } from '@flash-global66/g-button';
import { GImage } from '@flash-global66/g-image';
import { GIconFont } from '@flash-global66/g-icon-font';
import { currencyConfigFormat } from '@/helpers/formatCurrency';
import { formatToLocalTime } from '@/shared/common';
import SlaMessage from '@/shared/quote/components/QuoterSLAMessage/index.vue';
import type { IProductCard } from '@/helpers/constants/productCards';
import type { SuccessType } from '../../types/success.type';
import { useTransactionSecureFooterText } from '@/modules/transaction/composables/useTransactionSecureFooterText';
import { useBetweenAccountsSuccess } from '@/modules/transaction/composables/useBetweenAccountsSuccess';
import BetweenAccountsSuccessCard from '@/modules/transaction/components/BetweenAccountsSuccessCard/index.vue';
import { useSuccessTransaction } from './useSuccessTransaction';

defineOptions({ name: 'SuccessTransaction' });
import { useBrebSendContext } from '@/modules/transaction/composables/useBrebSendContext';

const props = defineProps({
  successType: {
    type: String as PropType<SuccessType | 'DEFAULT'>,
    default: 'DEFAULT',
  },
  cardProducts: {
    type: Array as PropType<IProductCard[]>,
    default: () => [],
  },
});

const {
  preTransactionResponse,
  quoteData,
  isBrazilPix,
  isComexFlow,
  purposeSelected,
  selectedPurposesComex,
  transactionPayload,
  isP2P,
  isStablecoin,
  selectedContact,
  selectedContactP2P,
  beneficiaryBank,
  beneficiaryAccountNumber,
  walletAddress,
  networkName,
  networkIcon,
  currencyIcon,
  currencyProductType,
  slaImmediate,
  title,
  actions,
  canDownloadVoucher,
  displayedTransactionId,
  downloadVoucher,
  goToTransaction,
  goToMovements,
  goToSend,
} = useSuccessTransaction(props);
const { secureFooterText } = useTransactionSecureFooterText();
const { isBrebSendContext } = useBrebSendContext();
const { isBetweenAccountsContext, details: betweenAccountsDetails } = useBetweenAccountsSuccess();
</script>

<template>
  <GStepGrid
    :actions="actions"
    :message-secure-text="secureFooterText"
    :max-width="isBrebSendContext ? 'max-w-[70rem]' : 'max-w-4xl'"
    hide-buttons
  >
    <template #content>
      <div
        class="m-auto max-w-lg grid grid-cols-1 py-xl gap-xl mb-lg"
        :class="
          isBrebSendContext
            ? 'lg:max-w-[70rem] lg:grid-cols-[1.2fr_1fr]'
            : 'lg:max-w-5xl lg:grid-cols-2'
        "
      >
      <div
        :class="isBrebSendContext ? 'flex flex-col gap-lg' : 'flex flex-col gap-xl'"
      >
        <BetweenAccountsSuccessCard v-if="isBetweenAccountsContext" :details="betweenAccountsDetails" />
        <section
          v-else
          class="flex flex-col w-full px-xl py-2xl bg-white rounded-md h-fit gap-2xl shadow-sm"
        >
          <div class="flex flex-col gap-4 items-center">
            <GImage name="paper-airplane" size="xl" class="mx-auto" />
            <h3
              class="text-center text-primary-txt font-bold"
              :class="isBrebSendContext ? 'text-7' : 'text-8'"
            >
              {{ title }}
            </h3>
          </div>

          <div class="flex flex-col gap-md">
            <p class="text-secondary-txt text-4 font-bold">
              Detalles del envío
            </p>
            <div class="h-px w-full bg-grey-50" />
            <dl class="flex flex-col gap-md">
              <div class="item">
                <dt class="key">ID de transacción</dt>
                <dd class="value">{{ displayedTransactionId }}</dd>
              </div>
              <div v-if="preTransactionResponse?.dateCreated" class="item">
                <dt class="key">
                  {{ isBrebSendContext ? 'Fecha' : 'Fecha de creación' }}
                </dt>
                <dd class="value">
                  {{
                    formatToLocalTime(
                      preTransactionResponse.dateCreated,
                      'DD [de] MMMM'
                    )
                  }}
                </dd>
              </div>
              <div v-if="preTransactionResponse.dateCreated" class="item">
                <dt class="key">Hora de creación</dt>
                <dd class="value">
                  {{
                    formatToLocalTime(
                      preTransactionResponse.dateCreated,
                      'hh:mm a'
                    )
                  }}
                </dd>
              </div>
              <div class="item">
                <dt class="key">
                  {{ isBrebSendContext ? 'Tu contacto recibe' : 'Monto' }}
                </dt>
                <dd class="value flex items-center justify-end gap-2">
                  <img v-if="currencyIcon" :src="currencyIcon" alt="" class="w-4 h-4 rounded-full" />
                  {{
                    currencyConfigFormat({
                      amount: preTransactionResponse.destinationAmount,
                      currencyCode: quoteData.destinationCurrency || 'USD',
                      productType: currencyProductType,
                      paymentType: 'WIRE_TRANSFER',
                      hasCode: true,
                    })
                  }}
                </dd>
              </div>
              <div class="item">
                <dt class="key">
                  {{ isBrebSendContext ? 'Beneficiario' : 'Destinatario' }}
                </dt>
                <dd v-if="!isP2P" class="value">
                  {{ selectedContact?.name }} {{ selectedContact?.lastName }}
                </dd>
                <dd v-else class="value">
                  {{ selectedContactP2P.name }}
                </dd>
              </div>
              <div v-if="beneficiaryBank" class="item">
                <dt class="key">Banco destino</dt>
                <dd class="value">{{ beneficiaryBank }}</dd>
              </div>
              <div v-if="beneficiaryAccountNumber" class="item">
                <dt class="key">N° de cuenta</dt>
                <dd class="value">{{ beneficiaryAccountNumber }}</dd>
              </div>
              <template v-if="isStablecoin">
                <div class="item">
                  <dt class="key">Dirección de wallet</dt>
                  <dd class="value break-all text-left ml-4">{{ walletAddress }}</dd>
                </div>
                <div class="item">
                  <dt class="key">Red</dt>
                  <dd class="value flex items-center justify-end gap-2">
                    <img v-if="networkIcon" :src="networkIcon" alt="" class="w-4 h-4 rounded-full" />
                    {{ networkName }}
                  </dd>
                </div>
              </template>
              <div
                v-if="transactionPayload?.purposeTransaction?.purpose.length"
                class="item"
              >
                <dt class="key">Propósito</dt>
                <dd class="value">
                  {{
                    isComexFlow
                      ? selectedPurposesComex.name
                      : purposeSelected?.name
                  }}
                </dd>
              </div>
              <div v-if="isComexFlow" class="item">
                <dt class="key">Numeral cambiario</dt>
                <dd class="value">
                  {{ selectedPurposesComex?.exchangeNumerals }}
                </dd>
              </div>
              <div
                v-if="isBrebSendContext && transactionPayload?.description"
                class="item"
              >
                <dt class="key">Comentario</dt>
                <dd class="value">{{ transactionPayload.description }}</dd>
              </div>
            </dl>
          </div>

          <SlaMessage
            :estimatedSla="preTransactionResponse?.expiryDate"
            :short-time="isBrazilPix"
            :immediate="slaImmediate"
          />
        </section>

        <section
          class="grid gap-2xl w-full px-md py-xs bg-white rounded-md h-fit shadow-sm"
          :class="{ 'grid-cols-3': canDownloadVoucher, 'grid-cols-2': !canDownloadVoucher }"
        >
          <GButton
            v-if="canDownloadVoucher"
            variant="tertiary"
            size="md"
            title="Descargar comprobante"
            data-test="button:descargar-comprobante"
            class="py-xs h-auto w-full"
            @click="downloadVoucher"
          >
            <div class="flex flex-col items-center justify-center text-terciary-txt gap-2 font-medium text-3">
              <GIconFont name="regular arrow-down-to-bracket" size="xl" />
              Descargar
            </div>
          </GButton>
          <GButton
            variant="tertiary"
            size="md"
            title="Enviar"
            data-test="button:ir-a-enviar"
            class="py-xs h-auto w-full"
            @click="goToTransaction"
          >
            <div class="flex flex-col items-center justify-center text-terciary-txt gap-2 font-medium text-3">
              <GIconFont
                :name="
                  isBrebSendContext ? 'regular arrow-up-from-bracket' : 'kit send'
                "
                size="xl"
              />
              Enviar
            </div>
          </GButton>
          <GButton
            variant="tertiary"
            size="md"
            title="Inicio"
            data-test="button:ir-al-inicio"
            class="py-xs h-auto w-full"
            @click="goToMovements"
          >
            <div class="flex flex-col items-center justify-center text-terciary-txt gap-2 font-medium text-3">
              <GIconFont name="regular house" size="xl" />
              Inicio
            </div>
          </GButton>
        </section>

        <GButton
          v-if="isBrebSendContext"
          variant="secondary"
          full
          data-test="button:ir-al-home"
          @click="goToMovements"
        >
          Ir al home
        </GButton>
        </div>

        <section>
          <p
            class="text-primary-txt font-semibold mb-sm"
            :class="isBrebSendContext ? 'text-5' : 'text-6'"
          >
            Con tu cuenta Global Business también podrás:
          </p>
          <div class="flex flex-col gap-xl">
            <template v-if="isBrebSendContext">
              <div class="flex flex-row p-lg gap-xl bg-white rounded-md shadow-sm">
                <GImage name="bolt" size="xl" class="self-center" />
                <div class="flex flex-col gap-xs">
                  <h1 class="font-semibold text-4 text-secondary-txt">
                    Realizar pagos instantáneos
                  </h1>
                  <p class="text-3 text-secondary-txt">
                    Realiza pagos gratuitos en segundos a otros usuarios, persona o
                    empresa de Global66 desde cualquiera de tus monedas en 7 países.
                  </p>
                  <GButton full class="mt-xs" @click="goToSend('P2P')">
                    Ir a Enviar
                  </GButton>
                </div>
              </div>
              <div class="flex flex-row p-lg gap-xl bg-white rounded-md shadow-sm">
                <GImage name="globe" size="xl" class="self-center" />
                <div class="flex flex-col gap-xs">
                  <h1 class="font-semibold text-4 text-secondary-txt">
                    Realizar transferencias nacionales e internacionales
                  </h1>
                  <p class="text-3 text-secondary-txt">
                    Paga en moneda local y en USD a proveedores, freelance y
                    colaboradores a +70 destinos, sin comisiones de envío y a un tipo
                    de cambio justo y transparente.
                  </p>
                  <GButton full class="mt-xs" @click="goToSend('TRANSACTION')">
                    Ir a Enviar
                  </GButton>
                </div>
              </div>
            </template>
            <template v-else>
              <div v-for="(product, idx) in cardProducts" :key="idx">
                <GProductCard :product="product" />
              </div>
            </template>
          </div>
        </section>
      </div>
    </template>
  </GStepGrid>
</template>

<style lang="scss" scoped>
@use './successTransaction.style.scss';
</style>
